"use client";

/**
 * Gentle one-time trial prompt for EXISTING eligible users - the
 * TrialOfferCard only lives on the homepage and /pricing, so a signed-in
 * free user who never visits either never learns the trial exists. This
 * slides up once (bottom sheet on mobile, bottom-right card on desktop)
 * and starts the trial in one tap.
 *
 * Timing ("kindly", not on-your-face): it asks after 10 s of dwell on
 * a page, and comes up only when the interruption budget gives it the
 * turn (lib/floating-slot): one offer per session, never on its first
 * page view (a visitor who just landed is never interrupted), never
 * over a result or a drawer.
 *
 * localStorage bookkeeping:
 *   menzil.trial.sheet.shown    - shown once EVER (set when it appears
 *     on screen, not when it asks: a turn it never got is no show)
 *   menzil.trial.offer.dismissed - shared with TrialOfferCard: a user
 *     who closed the card has answered; the sheet stays silent too
 *     (and closing the sheet silences the card, same key).
 *
 * Self-hides on /pricing and the homepage (the full timeline card is
 * there) and on /auth/* (never interrupt an auth flow).
 */
import { useEffect, useRef, useState } from "react";
import { FLOAT_CARD } from "@/lib/ui-classes";
import { useInterruption } from "@/lib/floating-slot";
import { usePathname } from "next/navigation";
import { isSeoEntryPath } from "@/lib/seo/paths";
import { useAuth } from "@/lib/auth-context";
import { captureEvent } from "@/lib/tracking";
import { useT } from "@/lib/locale-context";
import { API_BASE } from "@/lib/api";
import { Crown, Loader2, X, Check } from "lucide-react";

const DISMISS_KEY = "menzil.trial.offer.dismissed";
const SHOWN_KEY = "menzil.trial.sheet.shown";
const DWELL_MS = 10_000;

export default function TrialOfferSheet() {
  const t = useT();
  const { user, setUser, trialMode } = useAuth();
  const cardMode = (user?.trial_mode ?? trialMode) === "card";
  const pathname = usePathname();
  // Asks for its turn once the dwell is over; on screen while it has it.
  const [due, setDue] = useState(false);
  const open = useInterruption("trial", due);
  // Came up once: it stays (across pages, and back after a drawer or a
  // result has closed) until closed.
  const cameUp = useRef(false);
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);

  const rawPath = pathname.replace(/^\/(az|ru|en)/, "") || "/";
  // Not on SEO entry pages either: they carry their own guidance.
  const excluded =
    rawPath === "/" || rawPath.startsWith("/pricing") || rawPath.startsWith("/auth") || isSeoEntryPath(rawPath);

  const eligible =
    !!user && user.pro_trial_eligible === true && user.email_verified !== false;

  useEffect(() => {
    if (cameUp.current) return undefined;
    setDue(false);
    if (!eligible || excluded) return undefined;
    try {
      if (localStorage.getItem(SHOWN_KEY) === "1") return undefined;
      if (localStorage.getItem(DISMISS_KEY) === "1") return undefined;
    } catch {
      return undefined; // private mode - skip quietly
    }
    const id = setTimeout(() => setDue(true), DWELL_MS);
    return () => clearTimeout(id);
    // Re-arm per navigation: a dwell counts on one page only.
  }, [pathname, eligible, excluded]);

  useEffect(() => {
    if (!open || cameUp.current) return;
    cameUp.current = true;
    try {
      localStorage.setItem(SHOWN_KEY, "1");
    } catch { /* ignore */ }
  }, [open]);

  if (!open) return null;

  const close = () => {
    setDue(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch { /* ignore */ }
  };

  async function start() {
    if (cardMode) {
      // Card cohort: /pricing owns the card-registration flow.
      const m = window.location.pathname.match(/^\/(az|ru|en)\b/);
      window.location.href = `/${m ? m[1] : "az"}/pricing`;
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/auth/trial/start`, {
        method: "POST",
        credentials: "include",
      });
      const d = await r.json().catch(() => null);
      if (d?.reason === "card_required") {
        const m = window.location.pathname.match(/^\/(az|ru|en)\b/);
        window.location.href = `/${m ? m[1] : "az"}/pricing`;
        return;
      }
      if (r.ok && d?.ok) {
        captureEvent("trial_started", { mode: "nocard" });
        setStarted(true);
        const me = await fetch(`${API_BASE}/auth/me`, { credentials: "include" })
          .then((x) => x.json()).catch(() => null);
        if (me?.user) setUser(me.user);
        setTimeout(() => setDue(false), 3500);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-x-0 z-[1095] px-4 sm:inset-x-auto sm:right-6 sm:px-0 sm:max-w-sm"
      style={{ bottom: "calc(4.5rem + env(safe-area-inset-bottom))" }}
      role="dialog"
      aria-label={t("trial.offer.title")}
    >
      <div className={`rounded-2xl p-4 ${FLOAT_CARD}`}>
        {started ? (
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
              <Check className="w-[18px] h-[18px]" />
            </span>
            <p className="text-sm font-semibold text-gray-900">{t("trial.sheet.started")}</p>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
                <Crown className="w-[18px] h-[18px]" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-900">{t("trial.offer.title")}</p>
                <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">
                  {cardMode ? t("trial.sheet.body.card") : t("trial.sheet.body")}
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label={t("onboard.dismiss")}
                className="shrink-0 p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2.5">
              <button
                type="button"
                onClick={start}
                disabled={busy}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("trial.offer.cta")}
              </button>
            </div>
            <p className="mt-2 text-center text-[11px] text-gray-400">{cardMode ? t("trial.offer.note.card") : t("trial.offer.note")}</p>
          </>
        )}
      </div>
    </div>
  );
}
